<template>
  <b-toast solid
           auto-hide-delay="30000"
           :toast-class="toastClass"
           :header-class="headerClass"
           :toaster="position"
           :no-auto-hide="noAutoHide"
           :id="id"
           :to="link"
           v-show="title.length > 0"
           @hide="clearDateTimeInterval"
           @hidden="onHidden"
           @show="onShow"
           @shown="autoClose">
    <div class="notification-body-wrapper"
         @click="onNotificationClick">
      <div class="d-flex flex-row align-items-center">
        <div class="notification-icon"
             :class="notificationIconClasses"
             @click="toInbox">
          <system-update-icon v-if="id === 'system'"/>
          <sms-icon v-if="id === 'sms'"/>
          <call-icon v-if="id === 'call'"/>
          <voicemail-icon v-if="id === 'voicemail'"/>
          <mention-icon v-if="id === 'mention'"/>
          <reminder-icon v-if="id === 'reminder'"/>
          <call-incoming-icon v-if="isCall"/>
        </div>
        <div class="notification-details"
             :class="id !== 'incomingCall' ? 'w-100' : 'flex-grow-1'"
             @click="toInbox">
          <div class="d-flex flex-grow-1 align-items-baseline w-100"
               v-if="getSource">
            <span class="mr-auto text-white pr-1 text-sm">
              {{ getSource }}
            </span>
          </div>
          <div class="d-flex flex-grow-1 align-items-baseline w-100">
            <!--b-img blank blank-color="#ff5555" class="mr-2" width="12" height="12"></b-img-->
            <strong class="mr-auto text-white title pr-1">
              {{ title }}
            </strong>
            <small class="mr-2 text-grey-82 time text-nowrap"
                   v-if="!isCall">
              {{ runningDateTime }}
            </small>
          </div>

          <!-- caller location -->
          <div class="d-flex flex-grow-1 align-items-baseline w-100"
               v-if="location">
            <span class="mr-auto text-white pr-1 text-sm">
              {{ location }}
            </span>
          </div>

          <div class="text-grey-81 message-body text-break d-flex w-100">
            <div class="flex-grow-1 d-flex align-items-center w-100">
              <component class="message-icon mr-1"
                         :is="messageIcon"
                         v-if="messageIcon"/>
              <span v-if="!isCall && message"
                    class="message-text"
                    v-html="$options.filters.sanitizeHtml($options.filters.nl2br(message, false))">
              </span>
              <span v-if="id === 'sms' && attachment && !message"
                    class="message-text">
                Inbound MMS
              </span>
              <div class="message-text row has-ring-group"
                   v-else-if="isCall && campaignName && ringGroupName">
                <div class="campaign-wrapper col-5">
                  <div class="campaign-name">{{ campaignName }}</div>
                </div>
                <div class="flex-grow-1">></div>
                <div class="ring-group-wrapper col-5">
                  <div class="ring-group-name">{{ ringGroupName }}</div>
                </div>
              </div>
              <span class="message-text d-flex"
                    v-else-if="isCall && campaignName && !ringGroupName">
                {{ campaignName }}
              </span>
            </div>
          </div>
        </div>

        <div class="d-flex justify-content-center align-items-center call-actions"
             v-if="shouldShowCallActions">
          <q-btn class="height-32 mr-2"
                 ripple
                 round
                 no-caps
                 @click="rejectCall">
            <cancel-call-icon width="32"
                              height="32"/>
            <q-tooltip anchor="top middle"
                       self="bottom middle">
              Decline
            </q-tooltip>
          </q-btn>
          <q-btn class="height-32"
                 ripple
                 round
                 no-caps
                 @click="answerCall">
            <q-tooltip anchor="top middle"
                       self="bottom middle">
              Answer
            </q-tooltip>
            <accept-call-icon width="32" height="32"/>
          </q-btn>
        </div>
      </div>
    </div>
  </b-toast>
</template>

<script>
import AcceptCallIcon from 'components/icons/accept-call-icon'
import CancelCallIcon from 'components/icons/cancel-call-icon'
import { get, isEmpty } from 'lodash'
import * as CommunicationSourceCallTypes from 'src/constants/communication-call-source-types'
import { getQueryString } from 'src/plugins/helpers/functions'
import {
  TeamInboxMixin,
  aclMixin,
  agentMixin,
  liveCallsMixin,
  mentionsMixin,
  notificationMixin,
  notificationQueueMixin,
  userMixin,
  visibilityMixin
} from 'src/plugins/mixins'
import { UNTHREADED } from 'src/store/teaminbox/teaminbox.store'
import { mapActions, mapState } from 'vuex'
import * as CommunicationCurrentStatus from 'src/constants/communication-current-status'
import { DialerStatus } from 'src/constants/dialer-status'
import { CREATOR_TYPE_FLOW } from 'src/constants/creator-types'

// Dialer states in which a ringing inbound call is no longer this agent's to
// answer — they picked it up, it was cancelled (caller hung up, another agent
// took it, ring timed out), or the dialer returned to idle — so its pop-up must
// come down.
const CALL_ENDED_DIALER_STATUSES = [
  DialerStatus.CALL_CONNECTED,
  DialerStatus.INVITE_CANCELLED,
  DialerStatus.READY
]

export default {
  name: 'action-notification',

  mixins: [
    notificationMixin,
    notificationQueueMixin,
    mentionsMixin,
    visibilityMixin,
    aclMixin,
    agentMixin,
    userMixin,
    TeamInboxMixin,
    liveCallsMixin
  ],

  components: {
    AcceptCallIcon,
    CancelCallIcon
  },

  props: {
    id: {
      required: false,
      type: String,
      default: 'system'
    },

    position: {
      required: false,
      type: String,
      default: 'b-toaster-bottom-right'
    }
  },

  data () {
    return {
      runningDateTime: null,
      runningDateTimeInterval: null,
      notificationListeners: {},
      teamInboxLink: null
    }
  },

  computed: {
    ...mapState(['notifications', 'dialer', 'users', 'ringGroups', 'sdkAnnouncedCommunicationId']),
    ...mapState('cache', ['currentCompany']),

    isCall () {
      return this.id === 'incomingCall'
    },

    toastClass () {
      const toastClass = { data: 'action-notification notification-border-round' }

      if (!this.isCall) {
        toastClass.data += ' bg-grey-80'
      }

      if (this.isCall) {
        toastClass.data += ' bg-blue-60-opaque position-relative background-blur incoming-call-notification'
      }

      return toastClass.data
    },

    headerClass () {
      const headerClass = { data: 'border-0 p-0' }

      if (!this.isCall) {
        headerClass.data += ' bg-grey-80'
      }

      if (this.isCall) {
        headerClass.data += ' bg-blue-60-opaque'
      }

      return headerClass.data
    },

    message () {
      const message = get(this.notifications[this.id], 'message', '')

      return this.parseMentionToView(message)
    },

    messageIcon () {
      return get(this.notifications[this.id], 'messageIcon', null)
    },

    title () {
      const title = get(this.notifications[this.id], 'title', '')

      const fixedTitle = this.$options.filters.fixPhone(title)

      if (!['sms', 'call'].includes(this.id) || !fixedTitle) {
        return title
      }

      return fixedTitle
    },

    dateTime () {
      return get(this.notifications[this.id], 'dateTime', '')
    },

    contactId () {
      return get(this.notifications[this.id], 'contactId', '')
    },

    communicationId () {
      return get(this.notifications[this.id], 'communicationId', '')
    },

    link () {
      if (['system', 'incomingCall'].includes(this.id)) {
        return null
      }

      // reminders created inside a team inbox carry a ring group — route there
      // when the user has access. otherwise open the contact: a reminder is an
      // engagement and isn't individually resolvable by the communication route.
      if (this.id === 'reminder') {
        if (this.ringGroupId && this.ringGroupId !== '' && this.teamInboxLink) {
          return {
            path: this.teamInboxLink.path
          }
        }

        return {
          path: `/contacts/${this.contactId}`
        }
      }

      if (this.ringGroupId && this.ringGroupId !== '' && this.teamInboxLink) {
        const queryString = getQueryString(this.$route.query)
        const communicationRouteId = this.viewMode === UNTHREADED ? `/${this.communicationId}` : ''

        return {
          path: `${this.teamInboxLink.path}${communicationRouteId}${queryString}`
        }
      }

      // a mention's communicationId is the note, which the communication route
      // can't load (api-core scopes notes out) — open it in the contact timeline
      if (this.id === 'mention') {
        return {
          path: `/contacts/${this.contactId}/mentions/${this.communicationId}`
        }
      }

      return {
        path: `/contacts/${this.contactId}/communications/${this.communicationId}`
      }
    },

    attachment () {
      return get(this.notifications[this.id], 'attachment', '')
    },

    noAutoHide () {
      return ['system', 'incomingCall'].includes(this.id)
    },

    campaignId () {
      return get(this.notifications[this.id], 'campaignId', null)
    },

    campaignName () {
      return get(this.notifications[this.id], 'campaignName', null)
    },

    ringGroupId () {
      return get(this.notifications[this.id], 'ringGroupId', null)
    },

    ringGroupName () {
      return get(this.notifications[this.id], 'ringGroupName', null)
    },

    phoneNumber () {
      return get(this.notifications[this.id], 'phoneNumber', null)
    },

    communication () {
      return get(this.notifications[this.id], 'communication', null)
    },

    contact () {
      return get(this.notifications[this.id], 'contact', null)
    },

    isValidPhoneShowInfo () {
      const dialerCommunicationId = get(this.dialer, 'communication.id', null)

      return this.id === 'incomingCall' &&
        this.communicationId === dialerCommunicationId &&
        !this.dialer.parkedCall
    },

    getSource () {
      if (!this.communication) {
        return ''
      }

      if (this.isColdTransferUser) {
        return 'Transfer to User'
      }

      if (this.isColdTransferRg) {
        return 'Transfer to Inbox'
      }

      if (this.isIntroduceToUser) {
        return 'Introduce to User'
      }

      if (this.isIntroduceToRg) {
        return 'Introduce to Inbox'
      }

      if (this.isAddToUser) {
        return 'Add to User'
      }

      if (this.isAddToRg) {
        return 'Add to Inbox'
      }

      if (this.isSequence) {
        return 'Call from Sequence'
      }

      if (this.isFlow) {
        return 'Call from Flow'
      }

      if (this.isCallWaiting) {
        return 'Call Waiting'
      }

      return 'New Inbound Call'
    },

    isSequence () {
      if (isEmpty(this.communication)) {
        return false
      }

      return this.communication.workflow_id
    },

    isFlow () {
      if (isEmpty(this.communication)) {
        return false
      }

      return this.communication.creator_type === CREATOR_TYPE_FLOW
    },

    isIntroduceToRg () {
      if (isEmpty(this.communication)) {
        return false
      }

      return this.callIsIntroduced && this.communication.last_call_source === CommunicationSourceCallTypes.SOURCE_ADD_RG
    },

    isIntroduceToUser () {
      if (isEmpty(this.communication)) {
        return false
      }

      return this.callIsIntroduced && this.communication.last_call_source === CommunicationSourceCallTypes.SOURCE_ADD_USER
    },

    isAddToRg () {
      if (isEmpty(this.communication)) {
        return false
      }

      return !this.callIsIntroduced && this.communication.last_call_source === CommunicationSourceCallTypes.SOURCE_ADD_RG
    },

    isAddToUser () {
      if (isEmpty(this.communication)) {
        return false
      }

      return !this.callIsIntroduced && this.communication.last_call_source === CommunicationSourceCallTypes.SOURCE_ADD_USER
    },

    callIsIntroduced () {
      if (isEmpty(this.communication)) {
        return false
      }

      return this.communication.is_introduce
    },

    isColdTransferRg () {
      if (isEmpty(this.communication)) {
        return false
      }

      return this.communication.last_call_source === CommunicationSourceCallTypes.SOURCE_COLD_RG
    },

    isColdTransferUser () {
      if (isEmpty(this.communication)) {
        return false
      }

      return this.communication.last_call_source === CommunicationSourceCallTypes.SOURCE_COLD_USER
    },

    notificationIconClasses () {
      return [
        this.id === 'system' ? 'system-update' : ''
      ]
    },

    location () {
      if (!this.communication || !this.contact) {
        return false
      }

      let city = this.communication.city || this.contact.cnam_city || null
      const state = this.communication.state || this.contact.cnam_state || null
      const country = this.communication.country || this.contact.cnam_country || null

      if (!city && !state && !country) {
        return 'Unknown Location'
      }

      return `${city || ''}${city && state ? ', ' : ''}${state || ''}${state && country ? ' - ' : ''} ${country || ''}`
    },

    shouldShowCallActions () {
      return this.id === 'incomingCall'
    },

    isMention () {
      return this.id === 'mention'
    }
  },

  created () {
    this.notificationListeners[this.id] = {
      updateCommunication: (communication) => {
        if (!this.checkCommunicationMatchesUserAccessibility(communication)) {
          return
        }

        const isCallNotInProgressOrIncoming = this.isCallNotInProgressOrIncoming(communication.disposition_status2, communication.current_status2)

        const isAddOrIntroduceOperation = (communication.is_introduce ||
          communication.last_call_source === CommunicationSourceCallTypes.SOURCE_ADD_USER ||
          communication.last_call_source === CommunicationSourceCallTypes.SOURCE_ADD_RG) &&
          communication.legc_uuid &&
          [CommunicationCurrentStatus.CURRENT_STATUS_GREETING_NEW, CommunicationCurrentStatus.CURRENT_STATUS_RINGING_NEW].includes(communication.legc_status)

        if (isCallNotInProgressOrIncoming && this.communicationId === communication.id && !isAddOrIntroduceOperation) {
          this.processRemoveFromNotification(communication)
        }
      }
    }
  },

  mounted () {
    // Initialize teamInboxLink if ringGroupId is available on mount
    if (this.ringGroupId && this.ringGroupId !== '') {
      this.updateTeamInboxLink(this.ringGroupId)
    }
  },

  methods: {
    ...mapActions([
      'setNotifications',
      'setShowPhone'
    ]),

    async updateTeamInboxLink (ringGroupId) {
      if (this.isMention || await this.checkInboxAccess(ringGroupId)) {
        this.teamInboxLink = {
          path: `/team-inboxes/${ringGroupId}/contacts/${this.contactId}/communications`
        }
      } else {
        this.teamInboxLink = null
      }
    },

    startNotificationListeners () {
      this.$VueEvent.listen('update_communication', this.notificationListeners[this.id].updateCommunication)
    },

    stopNotificationListeners () {
      this.$VueEvent.stop('update_communication', this.notificationListeners[this.id].updateCommunication)
    },

    onShow () {
      console.log('Communication when event Show notification triggers: ', this.communication)
      this.stopNotificationListeners()
      this.startNotificationListeners()
    },

    autoClose () {
      console.log('Communication when event Auto close notification triggers: ', this.communication)
      this.runDateTimeInterval()

      // b-toast defers its own show behind a requestAnimationFrame callback, which
      // does not run while the tab is hidden, so a toast asked for there is only
      // rendered on return — by which time the notification it described may have
      // been cleared. Nothing below can retire it: the branches route through
      // `communication`, and every consumer of that ignores a null.
      if (this.isCall && !this.communication) {
        this.onHidden()
        this.$closeActionNotification(this.id)
        return
      }

      if (this.id === 'incomingCall' && document.getElementById('incomingCall') &&
        isEmpty(this.dialer.call)) {
        this.processRemoveFromNotification(this.communication)
        return
      }

      if (this.closeEndedIncomingCall()) {
        return
      }

      if (!this.noAutoHide &&
        this.dateTime &&
        this.dateTime.diff(this.$moment(), 'seconds') <= -30) {
        this.onHidden()
        this.$closeActionNotification(this.id)
        return
      }

      if (!this.title) {
        this.onHidden()
        this.$closeActionNotification(this.id)
        return
      }

      // missed call / voicemail notifications use id='call' — no ding per TEL-933
      if (this.id !== 'call') {
        this.playAudio()
      }
    },

    // Takes the incoming-call pop-up down once the dialer says the call is over
    // for this agent.
    //
    // A terminal status belongs to whichever call the dialer itself last held, not
    // necessarily the one on screen — `resetCall`, a re-registration and an accept
    // all reach one for their own call. So defer to the Twilio-ring anchor, as
    // `closeCallNotifications` does: while Twilio rings this agent for the
    // presented call, the pop-up stays. Every intended close clears the anchor
    // first, so no finished call's pop-up is held open.
    //
    // Returns whether it closed, keeping `autoClose`'s early-return shape; a false
    // there falls through as a non-terminal status already does.
    closeEndedIncomingCall () {
      // Only a pop-up this tab's Voice SDK raised can be judged by dialer status.
      // The HubSpot softphone raises its own, and an agent answering on a
      // forwarded phone gets one from the inbound broadcast with no browser leg
      // behind it — there a cleared anchor means unknown, not "call is over".
      if (this.id !== 'incomingCall' ||
        !this.communicationId ||
        this.sdkAnnouncedCommunicationId !== this.communicationId ||
        this.incomingTwilioRing === this.communicationId ||
        !CALL_ENDED_DIALER_STATUSES.includes(this.dialer.currentStatus)) {
        return false
      }

      this.onHidden()
      this.$closeActionNotification(this.id)

      return true
    },

    runDateTimeInterval () {
      if (this.dateTime) {
        this.runningDateTime = this.$options.filters.shortDateTimePassed(this.dateTime, false)
        this.runningDateTimeInterval = setInterval(() => {
          this.runningDateTime = this.$options.filters.shortDateTimePassed(this.dateTime, false)
        }, 60000)
      }
    },

    clearDateTimeInterval () {
      clearInterval(this.runningDateTimeInterval)
    },

    onHidden () {
      console.log('Communication when event Hidden notification triggers: ', this.communication)
      if (this.id === 'call') {
        return
      }

      this.clearDateTimeInterval()
      this.stopNotificationListeners()

      this.setNotifications({
        type: this.id,
        data: {
          title: '',
          message: '',
          messageIcon: null,
          attachment: null,
          dateTime: null,
          contactId: '',
          communicationId: '',
          campaignId: '',
          campaignName: '',
          ringGroupName: '',
          phoneNumber: '',
          communication: null,
          contact: null,
          queue: null
        }
      })

      // the slot is free now — surface the next queued reminder, if any.
      // deferred: BToast's afterLeave hook sets doRender = false right after
      // emitting hidden, so a synchronous re-show from inside this handler
      // gets clobbered and the toast never renders again this session
      if (this.id === 'reminder') {
        this.$nextTick(() => {
          this.$drainReminderNotifications()
        })
      }
    },

    type () {
      switch (this.id) {
        case 'sms':
          return 'messages'
        case 'call':
        case 'callVoicemail':
          return 'calls'
        case 'voicemail':
          return 'voicemails'
        case 'mention':
          return 'mentions'
      }
      return 'sms'
    },

    answerCall () {
      if (this.dialer.currentStatus === 'WRAP_UP') {
        this.$VueEvent.fire('endWrapUp')
      }

      this.$VueEvent.fire('answerCall')
      this.setShowPhone(true)
    },

    rejectCall () {
      this.closeCallNotifications(this.id, this.communicationId, true)
      this.$VueEvent.fire('rejectCall')
    },

    onNotificationClick (event) {
      // check if there are call buttons in the notification
      const found = document.querySelector('.notification-body-wrapper .call-actions')

      if (!found &&
        this.id === 'system') {
        this.$closeActionNotification(this.id)
      }
    },

    toInbox (event) {
      if (this.isValidPhoneShowInfo) {
        return
      }

      let found = document.querySelector('.notification-body-wrapper .notification-icon:not(.system-update)')

      if (!found) {
        return
      }

      // Get the path from the link computed property
      const linkPath = this.link?.path
      if (linkPath && this.$route.path !== linkPath) {
        this.$router.push({ path: linkPath })
      }
    },

    toContact () {
      if (!this.isCall) {
        return
      }

      if (this.isValidPhoneShowInfo) {
        return
      }

      const className = { data: null }
      const found = event.path.find((item) => {
        className.data = get(item, 'className', null)
        return className.data && typeof className.data === 'string' && (className.data.includes('notification-details'))
      })

      if (!found) {
        return
      }

      if (this.$route.path !== `/contacts/${this.contactId}`) {
        this.$router.push({
          path: `/contacts/${this.contactId}`
        })
      }
    }
  },

  watch: {
    // The close used to run only from `autoClose`, which b-toast invokes from its
    // `shown` event — emitted inside a requestAnimationFrame callback that does
    // not run in a hidden tab. An agent working in another tab therefore kept a
    // pop-up for a call that had already ended, then saw it vanish the moment they
    // focused Talk (reported as a pop-up that flickers, or lingers long after
    // another agent answered). Dialer status is plain reactive state, so watching
    // it reaches the close with the tab focused or not.
    //
    // What clears the pop-up from screen is `onHidden()` emptying the stored
    // notification: `title` becomes '' and the toast's `v-show` applies
    // `display: none` on the next render, no transition needed. Keep that binding
    // — `$closeActionNotification` is `$bvToast.hide()`, whose leave transition is
    // rAF-backed too, so it is inert here; the dialer already calls it on invite
    // cancel and the pop-up still lingered.
    'dialer.currentStatus': function () {
      this.closeEndedIncomingCall()
    },
    ringGroupId: {
      immediate: true,
      handler: function (newRingGroupId) {
        if (newRingGroupId && newRingGroupId !== '') {
          this.updateTeamInboxLink(newRingGroupId)
        } else {
          this.teamInboxLink = null
        }
      }
    },
    contactId: {
      immediate: true,
      handler: function (contactId) {
        if (contactId && this.ringGroupId) {
          this.updateTeamInboxLink(this.ringGroupId)
        }
      }
    }
  },

  beforeDestroy () {
    this.clearDateTimeInterval()
    this.stopNotificationListeners()
  }
}
</script>
